import React, { useEffect, useState, useRef } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Upload, Plus, UserX, UserCheck, FileText } from "lucide-react";

export default function Employees() {
  const { user } = useAuth();
  const canManage = ["Admin", "Accounts"].includes(user?.role);
  const canAllocate = ["Admin", "ProjectManager"].includes(user?.role);
  const [employees, setEmployees] = useState([]);
  const [projects, setProjects] = useState([]);
  const [costCentres, setCostCentres] = useState([]);
  const [allocOpen, setAllocOpen] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  const [addForm, setAddForm] = useState({ employee_code: "", name: "", designation: "", department: "", email: "", phone: "", date_of_joining: "", reporting_manager: "" });
  const [showInactive, setShowInactive] = useState(false);
  const fileRef = useRef();

  const load = async () => {
    const [e, p, c] = await Promise.all([api.get("/employees"), api.get("/projects"), api.get("/cost-centres")]);
    setEmployees(e.data); setProjects(p.data); setCostCentres(c.data);
  };
  useEffect(() => { load(); }, []);

  const upload = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    try {
      const fd = new FormData(); fd.append("file", f);
      const r = await api.post("/employees/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const skipped = (r.data.skipped_rows || []).length;
      toast.success(`Imported ${r.data.total_rows} rows: ${r.data.created} created · ${r.data.updated} updated${skipped ? ` · ${skipped} skipped` : ""}`);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Import failed"); }
    finally { if (fileRef.current) fileRef.current.value = ""; }
  };

  const addEmployee = async (e) => {
    e.preventDefault();
    try {
      await api.post("/employees", { ...addForm, date_of_joining: addForm.date_of_joining || null });
      toast.success(`Employee ${addForm.employee_code} added`);
      setAddOpen(false);
      setAddForm({ employee_code: "", name: "", designation: "", department: "", email: "", phone: "", date_of_joining: "", reporting_manager: "" });
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const downloadTemplate = async () => {
    try {
      const r = await api.get("/employees/template.xlsx", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement("a");
      a.href = url; a.download = "employees-roster-template.xlsx"; a.click();
      window.URL.revokeObjectURL(url);
      toast.success("Template downloaded");
    } catch (err) { toast.error(err.response?.data?.detail || "Template download failed"); }
  };

  const toggleActive = async (emp) => {
    const next = (emp.status === "inactive") ? "active" : "inactive";
    if (next === "inactive" && !window.confirm(`Mark ${emp.name} as inactive? They will be hidden from allocations and salary imports.`)) return;
    try {
      await api.patch(`/employees/${emp.id}`, { status: next });
      toast.success(next === "inactive" ? "Marked inactive" : "Reactivated");
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const visible = employees.filter((e) => showInactive || (e.status || "active") !== "inactive");

  return (
    <div className="p-6 lg:p-8">
      <div className="flex items-start justify-between mb-6 gap-4 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-widest text-slate-500 font-medium">Directory</div>
          <h1 className="font-display text-3xl font-extrabold mt-1">Employees</h1>
          <p className="text-sm text-slate-500 mt-1">Upload the roster from Excel or CSV. Duplicates are deduped on <code className="font-mono text-[11px]">employee_code</code>. Mark inactive when someone leaves.</p>
        </div>
        {canManage && (
          <div className="flex gap-2 flex-wrap">
            <input type="file" ref={fileRef} accept=".csv,.xlsx" onChange={upload} className="hidden" data-testid="emp-csv-input" />
            <Button onClick={() => fileRef.current?.click()} className="bg-[#0F172A]" data-testid="emp-import-btn"><Upload className="w-4 h-4 mr-2" /> Upload Roster (.xlsx / .csv)</Button>
            <Button variant="outline" onClick={downloadTemplate} data-testid="emp-template-btn"><FileText className="w-4 h-4 mr-2" /> Download Template</Button>
            <Button variant="outline" onClick={() => setAddOpen(true)} data-testid="emp-add-btn"><Plus className="w-4 h-4 mr-2" /> Add Employee</Button>
          </div>
        )}
      </div>

      <div className="flex items-center gap-3 mb-3 text-xs text-slate-600">
        <label className="flex items-center gap-2"><input type="checkbox" checked={showInactive} onChange={(e) => setShowInactive(e.target.checked)} data-testid="show-inactive-toggle" /> Show inactive</label>
        <span className="text-slate-400">·</span>
        <span>{visible.length} of {employees.length}</span>
      </div>

      <Card>
        <CardContent className="p-0 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-xs uppercase text-slate-500">
              <tr className="border-b">
                <th className="text-left px-4 py-2.5">Emp Code</th>
                <th className="text-left px-4 py-2.5">Name</th>
                <th className="text-left px-4 py-2.5">Designation</th>
                <th className="text-left px-4 py-2.5">Department</th>
                <th className="text-left px-4 py-2.5">Allocations</th>
                <th className="text-left px-4 py-2.5">Status</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {visible.length === 0 && (
                <tr><td colSpan={7} className="px-4 py-10 text-center text-slate-500">No employees. Upload a roster or add one manually.</td></tr>
              )}
              {visible.map(e => {
                const total = (e.allocations || []).reduce((s, a) => s + (a.allocation_pct || 0), 0);
                const inactive = e.status === "inactive";
                return (
                  <tr key={e.id} className={`border-b border-slate-100 ${inactive ? "opacity-60" : ""}`} data-testid={`emp-row-${e.employee_code}`}>
                    <td className="px-4 py-2.5 font-mono text-xs">{e.employee_code}</td>
                    <td className="px-4 py-2.5 font-medium">{e.name}</td>
                    <td className="px-4 py-2.5 text-slate-600">{e.designation || "—"}</td>
                    <td className="px-4 py-2.5 text-slate-600">{e.department || "—"}</td>
                    <td className="px-4 py-2.5"><Badge variant={total === 100 ? "outline" : "destructive"} className="font-mono">{total}%</Badge></td>
                    <td className="px-4 py-2.5">
                      <Badge className={`text-[10px] ${inactive ? "bg-red-100 text-red-700" : "bg-emerald-100 text-emerald-700"}`}>{inactive ? "Inactive" : "Active"}</Badge>
                    </td>
                    <td className="px-4 py-2.5 flex gap-1 justify-end">
                      {canAllocate && !inactive && <Button size="sm" variant="ghost" onClick={() => setAllocOpen(e)} data-testid={`emp-alloc-${e.employee_code}`}>Allocations</Button>}
                      {canManage && (
                        <Button size="sm" variant="ghost" onClick={() => toggleActive(e)} data-testid={`emp-toggle-${e.employee_code}`} className={inactive ? "text-emerald-700" : "text-red-600"}>
                          {inactive ? <><UserCheck className="w-3.5 h-3.5 mr-1" /> Reactivate</> : <><UserX className="w-3.5 h-3.5 mr-1" /> Disable</>}
                        </Button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </CardContent>
      </Card>

      <Dialog open={!!allocOpen} onOpenChange={(v) => !v && setAllocOpen(null)}>
        <DialogContent className="max-w-2xl">
          {allocOpen && <AllocEditor emp={allocOpen} projects={projects} costCentres={costCentres} onSave={() => { setAllocOpen(null); load(); }} />}
        </DialogContent>
      </Dialog>

      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent data-testid="emp-add-dialog">
          <DialogHeader><DialogTitle>Add Employee</DialogTitle></DialogHeader>
          <form onSubmit={addEmployee} className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Employee Code *</Label><Input required value={addForm.employee_code} onChange={(e) => setAddForm({ ...addForm, employee_code: e.target.value })} data-testid="emp-add-code" /></div>
              <div><Label>Name *</Label><Input required value={addForm.name} onChange={(e) => setAddForm({ ...addForm, name: e.target.value })} data-testid="emp-add-name" /></div>
              <div><Label>Designation</Label><Input value={addForm.designation} onChange={(e) => setAddForm({ ...addForm, designation: e.target.value })} /></div>
              <div><Label>Department</Label><Input value={addForm.department} onChange={(e) => setAddForm({ ...addForm, department: e.target.value })} /></div>
              <div><Label>Email</Label><Input type="email" value={addForm.email} onChange={(e) => setAddForm({ ...addForm, email: e.target.value })} /></div>
              <div><Label>Phone</Label><Input value={addForm.phone} onChange={(e) => setAddForm({ ...addForm, phone: e.target.value })} /></div>
              <div><Label>Date of Joining</Label><Input type="date" value={addForm.date_of_joining} onChange={(e) => setAddForm({ ...addForm, date_of_joining: e.target.value })} /></div>
              <div><Label>Reporting Manager</Label><Input value={addForm.reporting_manager} onChange={(e) => setAddForm({ ...addForm, reporting_manager: e.target.value })} /></div>
            </div>
            <DialogFooter><Button type="submit" className="bg-[#0F172A]" data-testid="emp-add-save">Add</Button></DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Card className="mt-6 bg-slate-50 border-dashed">
        <CardContent className="p-5">
          <h3 className="font-semibold text-sm">Roster File Format (.xlsx or .csv)</h3>
          <p className="text-xs text-slate-600 mt-1 font-mono">employee_code · name · designation · department · email · phone · date_of_joining · reporting_manager · status</p>
          <p className="text-xs text-slate-500 mt-1">Duplicates are deduped on <b>employee_code</b>: matching rows update in place, new rows are inserted.</p>
        </CardContent>
      </Card>
    </div>
  );
}

function AllocEditor({ emp, projects, costCentres, onSave }) {
  const [rows, setRows] = useState(emp.allocations || []);
  const total = rows.reduce((s, r) => s + parseFloat(r.allocation_pct || 0), 0);
  const add = () => setRows([...rows, { project_id: "", cost_centre_id: "", allocation_pct: 0, role: "" }]);
  const upd = (i, k, v) => setRows(rows.map((r, idx) => idx === i ? { ...r, [k]: v } : r));
  const rm = (i) => setRows(rows.filter((_, idx) => idx !== i));
  const save = async () => {
    const clean = rows.filter(r => r.project_id || r.cost_centre_id).map(r => ({ ...r, allocation_pct: parseFloat(r.allocation_pct || 0), project_id: r.project_id || null, cost_centre_id: r.cost_centre_id || null }));
    await api.post(`/employees/${emp.id}/allocations`, clean);
    toast.success("Saved");
    onSave();
  };
  return (
    <div>
      <DialogHeader><DialogTitle>Allocations · {emp.name}</DialogTitle></DialogHeader>
      <div className="space-y-2 mt-4">
        {rows.map((r, i) => (
          <div key={i} className="flex gap-2 items-center">
            <Select value={r.project_id || ""} onValueChange={(v) => { upd(i, "project_id", v); upd(i, "cost_centre_id", ""); }}>
              <SelectTrigger className="w-48"><SelectValue placeholder="Project" /></SelectTrigger>
              <SelectContent>{projects.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
            </Select>
            <span className="text-slate-400 text-xs">or</span>
            <Select value={r.cost_centre_id || ""} onValueChange={(v) => { upd(i, "cost_centre_id", v); upd(i, "project_id", ""); }}>
              <SelectTrigger className="w-48"><SelectValue placeholder="Cost Centre" /></SelectTrigger>
              <SelectContent>{costCentres.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
            </Select>
            <Input type="number" value={r.allocation_pct} onChange={(e) => upd(i, "allocation_pct", e.target.value)} className="w-20" placeholder="%" />
            <Button size="sm" variant="ghost" onClick={() => rm(i)}>×</Button>
          </div>
        ))}
        <Button size="sm" variant="outline" onClick={add}>+ Add allocation</Button>
      </div>
      <div className="mt-4 flex justify-between items-center text-sm">
        <div>Total: <span className={`font-mono font-bold ${total === 100 ? "text-emerald-600" : "text-red-600"}`}>{total}%</span> {total !== 100 && <span className="text-red-600 text-xs">(must equal 100% for salary posting)</span>}</div>
        <Button onClick={save} data-testid="save-alloc-btn">Save</Button>
      </div>
    </div>
  );
}
